import {FormSubmitHandler} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useGetTireLoadIndexQuery,
  usePatchTireLoadIndexMutation,
  usePostTireLoadIndexMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {TireDictionaryForm} from '../../components/TireDictionaryForm/TireDictionaryForm';
import {TireDictionaryFormType} from '../../types/TireDictionaryFormType';

export function TireInventoryLoadIndexDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetTireLoadIndexQuery(
    {tireLoadIndexId: id ?? ''},
    {skip: isNil(id)}
  );
  const [postTireLoadIndex] = usePostTireLoadIndexMutation();
  const [patchTireLoadIndex] = usePatchTireLoadIndexMutation();

  const handleSubmit: FormSubmitHandler<TireDictionaryFormType> = async (
    formData,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {name: formData.name};

    if (isNil(id)) {
      return await postTireLoadIndex({
        body,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.tireLoadIndex);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchTireLoadIndex({
      tireLoadIndexId: id,
      body,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.tireLoadIndex);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      header={{
        title: data?.name ?? i18n.t('entity.tiresInventory.labels.newTireLoadIndex'),
        breadcrumbs: [
          {
            label: i18n.t('module.tiresInventory.title'),
            isCurrentPage: true,
          },
          {
            label: i18n.t('entity.tiresInventory.labels.tireLoadIndex'),
            href: settingsRoutes.tireLoadIndex,
          },
        ],
      }}
      isLoading={isLoading}
      isError={isError}
      isCreating={isNil(id)}
      data-testid={testIds.settings.tireLoadIndexDetail('template')}
    >
      <TireDictionaryForm
        onSubmit={handleSubmit}
        defaultValues={{name: data?.name}}
        onDiscard={(navigateWithoutBlocker) => navigateWithoutBlocker(settingsRoutes.tireLoadIndex)}
        data-testid={testIds.settings.tireLoadIndexDetail('form')}
      />
    </SettingsTemplate>
  );
}
